import {
  SingleResponse,
  parseErrorContainer,
  type JsonHelper,
} from "../../tastyworks"
import type HttpClient from "../../tastyworks/http"
import {
  ATTEMPTS_REMAINING_HEADER,
  AUTHORIZATION,
  CSRF_HEADER,
  CSRF_HEADER_VALUE,
  DEVICE_CHALLENGE_TOKEN_HEADER,
  DeviceChallengeError,
  InvalidAnswerError,
  InvalidSessionError,
  OTP_HEADER,
  RECOVERY_CODE_HEADER,
  TooManyRequestsError,
  TwoFactorAuthError,
  isDeviceChallengeResponse,
  isInvalidCredentialsResponse,
  isOTPResponse,
  isTooManyRequestsResponse,
  toJsonHelper,
} from "../../tastyworks/http"
import type Logger from "../../tastyworks/logger"
import { toFormData, type Params } from "../../tastyworks/request"
import {
  parseSingleResponse,
  updateSingleResponse,
} from "../../tastyworks/response"
import {
  AccountStreamerToken,
  ACCOUNT_STREAMER_TOKEN_DESER,
  ClientDomain,
  LOGIN_PARSER,
  parseUser,
  type TwLogin,
} from "../../tastyworks/session"
import TwUser, { USER_DESER } from "../../tastyworks/user"

export interface ValidateOptions {
  /**
   * Raw-fetch the probe instead of routing through the HttpClient, so an
   * invalid session yields an empty response rather than firing the
   * SessionErrorHandler redirect.
   */
  skipHandlers?: boolean
}

export class SessionService {
  private readonly sessionsClient: HttpClient

  constructor(
    baseHttpClient: HttpClient,
    private readonly logger: Logger
  ) {
    this.sessionsClient = baseHttpClient.nested("sessions")
  }

  readonly login = async (
    login: string,
    password: string,
    clientDomain: ClientDomain = ClientDomain.Customer,
    deviceChallengeToken?: string
  ): Promise<SingleResponse<TwLogin>> => {
    const url = this.sessionsClient.baseUrl.toUrl("")
    const useCookieAuth = this.sessionsClient.currentTransport === "cookie-csrf"

    const params: Params = { login, password, clientDomain }
    if (useCookieAuth) {
      params["use-cookies"] = true
    }
    const body = toFormData(params)

    const castleTokenHeader =
      await this.sessionsClient.session.getCastleHeaders()
    // Do a raw fetch request to bypass SessionErrorHandler
    const headers: Record<string, string> = {
      ...castleTokenHeader,
    }
    if (deviceChallengeToken) {
      headers[DEVICE_CHALLENGE_TOKEN_HEADER] = deviceChallengeToken
    }
    // Recovery code and OTP are mutually exclusive - recovery code takes precedence
    if (this.sessionsClient.session.recoveryCode.length > 0) {
      headers[RECOVERY_CODE_HEADER] = this.sessionsClient.session.recoveryCode
    } else if (this.sessionsClient.session.oneTimePassword.length > 0) {
      headers[OTP_HEADER] = this.sessionsClient.session.oneTimePassword
    }

    const requestInit: RequestInit = { body, headers, method: "POST" }
    if (useCookieAuth) {
      requestInit.credentials = "include"
    }
    const fetchResponse = await this.sessionsClient.fetcher(url, requestInit)

    const helper = await toJsonHelper(fetchResponse)
    const response = parseSingleResponse(helper, LOGIN_PARSER)

    if (response.isError) {
      const attemptsRemaining = fetchResponse.headers.get(
        ATTEMPTS_REMAINING_HEADER
      )
      if (
        isDeviceChallengeResponse(fetchResponse, response.errorContainer.code)
      ) {
        const deviceChallengeHeader = fetchResponse.headers.get(
          DEVICE_CHALLENGE_TOKEN_HEADER
        )!

        throw new DeviceChallengeError(
          response.errorContainer.code,
          response.errorContainer.message,
          deviceChallengeHeader,
          fetchResponse
        )
      }

      if (isOTPResponse(fetchResponse)) {
        throw new TwoFactorAuthError(
          "POST",
          url,
          fetchResponse,
          helper,
          attemptsRemaining
        )
      }

      if (isTooManyRequestsResponse(fetchResponse)) {
        const res = new SingleResponse<TwLogin>()
        parseErrorContainer(helper, res)

        throw new TooManyRequestsError(
          "POST",
          url,
          fetchResponse,
          res.errorContainer.message
        )
      }
      if (isInvalidCredentialsResponse(response.errorContainer)) {
        throw new InvalidAnswerError(
          response.errorContainer.message,
          attemptsRemaining
        )
      }
    }

    return response
  }
  /**
   * Validates the current session against the `validate` endpoint.
   *
   * By default the request goes through the HttpClient, so an invalid session
   * fires the SessionErrorHandler (the app's invalid-session redirect). Pass
   * `{ skipHandlers: true }` for a silent probe that raw-fetches instead: an
   * invalid session then yields an empty response for the caller to act on
   * rather than redirecting — used during bootstrap, where callers decide what
   * to do with a missing user themselves.
   */
  readonly validate = async (
    sessionToken: string,
    options: ValidateOptions = {}
  ): Promise<SingleResponse<TwUser>> => {
    return options.skipHandlers
      ? this.validateWithoutHandlers(sessionToken)
      : this.validateWithHandlers(sessionToken)
  }

  private readonly validateWithHandlers = async (
    sessionToken: string
  ): Promise<SingleResponse<TwUser>> => {
    if (!sessionToken) {
      throw new Error("sessionToken required")
    }

    let helper: JsonHelper | null = null
    try {
      helper = await this.sessionsClient.postJson("validate", {})
    } catch (err) {
      if (
        !(
          err instanceof InvalidSessionError ||
          err instanceof TwoFactorAuthError
        )
      ) {
        throw err
      }
      helper = err.jsonHelper || (await toJsonHelper(err.response))
    }

    const user = new TwUser()
    const response = updateSingleResponse(helper, user, USER_DESER.update)
    user.sessionToken = sessionToken

    return response
  }

  readonly mintAccountStreamerToken = async (): Promise<
    SingleResponse<AccountStreamerToken>
  > => {
    const helper = await this.sessionsClient.postJson("single-use-tokens", {})
    const streamerToken = new AccountStreamerToken()
    return updateSingleResponse(
      helper,
      streamerToken,
      ACCOUNT_STREAMER_TOKEN_DESER.update
    )
  }

  /**
   * Transport-aware silent probe: sends the bearer token or the cookie+CSRF
   * pair per the client's transport, via a raw fetch that bypasses the
   * SessionErrorHandler. A non-ok status yields an empty response instead of
   * the invalid-session redirect. In cookie mode the HttpOnly cookie carries
   * the session, so `sessionToken` is only used for the bearer header.
   */
  private readonly validateWithoutHandlers = async (
    sessionToken: string
  ): Promise<SingleResponse<TwUser>> => {
    const url = this.sessionsClient.baseUrl.toUrl("validate")
    const useCookieAuth = this.sessionsClient.currentTransport === "cookie-csrf"

    const authHeaders: HeadersInit = useCookieAuth
      ? { [CSRF_HEADER]: CSRF_HEADER_VALUE }
      : { [AUTHORIZATION]: sessionToken }
    const castleHeaders = await this.sessionsClient.session.getCastleHeaders()

    const requestInit: RequestInit = {
      method: "POST",
      headers: { ...authHeaders, ...castleHeaders },
    }
    if (useCookieAuth) {
      requestInit.credentials = "include"
    }

    const fetchResponse = await this.sessionsClient.fetcher(url, requestInit)
    if (!fetchResponse.ok) {
      return new SingleResponse<TwUser>()
    }

    const helper = await toJsonHelper(fetchResponse)
    return parseSingleResponse(helper, parseUser)
  }

  readonly validateOneTimePassword = async (
    oneTimePassword: string
  ): Promise<SingleResponse<null>> => {
    this.sessionsClient.session.oneTimePassword = oneTimePassword
    try {
      await this.sessionsClient.postJson("validate-one-time-password", {})
      return new SingleResponse<null>()
    } catch (err) {
      if (
        !(
          err instanceof InvalidSessionError ||
          err instanceof TwoFactorAuthError
        )
      ) {
        throw err
      }
      const response = new SingleResponse<null>()
      const helper = await toJsonHelper(err.response)
      parseErrorContainer(helper, response)
      return response
    }
  }

  readonly logout = async (sessionToken: string): Promise<boolean> => {
    if (!sessionToken) {
      return false
    }

    const url = this.sessionsClient.baseUrl.toUrl("")
    try {
      await this.sessionsClient.fetch(url, "DELETE")
      return true
    } catch (error) {
      this.logger.warn("Error on logout", error)
      return false
    }
  }

  async requestOneTimePassword(
    resetPasswordToken: string | undefined = undefined
  ): Promise<any> {
    return this.sessionsClient.postJson(
      "one-time-password-request",
      {},
      resetPasswordToken ? { resetPasswordToken } : undefined
    )
  }
}
